import {Link, useLocation} from '@github-ui/react-router'
import {NavList, Tooltip} from '@primer/react'
import type {ComponentProps} from 'react'
import type React from 'react'

type SidebarRowProps = {
  icon: React.ElementType
  title: string
  id: string
  path: string
  tooltip: string
  onClick?: () => void
}

type TooltipLinkProps = ComponentProps<typeof Link> & {
  tooltip: string
  ref?: React.Ref<HTMLAnchorElement>
}

/**
 * Rendered as `NavList.Item`'s polymorphic `as` component so the Tooltip wraps
 * only the anchor Primer already places inside the `<li>`. Wrapping `NavList.Item`
 * itself would make the Tooltip's sibling popover `<span>` a direct child of the
 * enclosing `<ul>`, which is invalid list markup and breaks list semantics for
 * assistive technology. Skips the Tooltip entirely when `tooltip` is empty (e.g.
 * `SidebarLoading`'s placeholder rows) since TooltipV2 always renders a popover
 * `<span>`/`aria-describedby`, even for empty text.
 */
function TooltipLink({tooltip, ref, ...linkProps}: TooltipLinkProps) {
  if (!tooltip) return <Link ref={ref} {...linkProps} />

  return (
    <Tooltip ref={ref} text={tooltip} type="description">
      <Link {...linkProps} />
    </Tooltip>
  )
}

export function SidebarRow({icon: Icon, title, id, path, tooltip, onClick}: SidebarRowProps) {
  // Read pathname via useLocation so the active-page indicator reacts to SPA
  // navigation. Reading ssrSafeLocation at render scope is frozen by the
  // React Compiler and leaves aria-current="page" stuck on the first route.
  const {pathname} = useLocation()
  const components = pathname.split('/')
  const suffix = components.length === 0 ? '' : components[components.length - 1]

  return (
    <NavList.Item
      as={TooltipLink}
      tooltip={tooltip}
      aria-current={suffix === id ? 'page' : undefined}
      to={path}
      onClick={onClick}
    >
      <NavList.LeadingVisual>
        <Icon />
      </NavList.LeadingVisual>
      {title}
    </NavList.Item>
  )
}
